import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { useDispatch } from "react-redux";
import { Form, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { userActions } from "../../store/userAuthSlice";
import "react-toastify/dist/ReactToastify.css";

import { useBranding } from "../../context/BrandingContext";
// adjust path if your hook lives somewhere else
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import BrandName from "../../components/common/BrandName";

const LOGIN_MUTATION = gql`
    mutation WebLogin($email: String!, $password: String!) {
        webLogin(email: $email, password: $password) {
            success
            errors
            token
            refreshToken
            user {
                id
                email
                mobile
                isStaff
                appSubscription {
                    plan {
                        id
                        name
                        application
                        features {
                            key
                        }
                    }
                    currency
                    stripeSubscriptionId
                }
                profiles {
                    id
                    firstName
                    lastName
                    gender
                    dob
                    activeInWeb
                    isActive
                    currency
                    logoUrl
                    profileOrganizations {
                        status
                        organization {
                            slug
                            name
                            id
                            createdBy {
                                id
                            }
                            logo
                            organizationType {
                                name
                                code
                            }
                            activeSubscription {
                                plan {
                                    id
                                    name
                                    features {
                                        key
                                    }
                                }
                            }
                            org {
                                name
                                organizationType {
                                    name
                                    code
                                }
                            }
                        }
                        group {
                            id
                            name
                            permissions {
                                codename
                            }
                        }
                    }
                }
                sponsors {
                    id
                    name
                }
            }
        }
    }
`;

function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // 🔹 Global branding + theme + portal flag + ready
    const { branding, theme, isOrgPortal, ready } = useBranding();
    const colors = useThemeColors();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [userLogin, { loading }] = useMutation(LOGIN_MUTATION);

    // 🧠 IMPORTANT: wait until BrandingProvider has resolved
    // - On root domain: ready = true, branding = null → base kBola theme
    // - On org subdomain: ready = true, branding != null → org theme
    if (!ready) {
        return null; // or a small loader if you want
    }

    // 🔹 Resolve org name to display in the header
    const orgName = isOrgPortal
        ? branding?.organization?.name || "Organization"
        : "AngeonX Sports";

    // 🔹 Resolve logo (org logo for portals, bright kBola variant for root)
    const logoSrc = isOrgPortal
        ? (
        branding?.logoUrl ||              // branding-specific logo
        branding?.organization?.logoUrl   // org logo from API
    ) || theme.logo.short                  // fallback to global short logo
        : "/dist/img/angeonx-sports.png";             // normal login: dark-background app logo

    return (
        <div
            style={{
                backgroundColor: colors.background,
                color: colors.textOnBackground,
                fontFamily: "'Poppins', sans-serif",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowX: "hidden",
            }}
        >
            <div className="login-box" style={{ width: "100%", maxWidth: 420 }}>
                <div
                    className="card card-outline"
                    style={{
                        backgroundColor: colors.surface,
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.3)",
                        border: `1px solid ${
                            colors.border || "rgba(255,255,255,0.06)"
                        }`,
                    }}
                >
                    {/* Header: logo + name / subtitle */}
                    <div className="card-header text-center">
                        {logoSrc && <NavLink to="/">
                            <img
                                src={logoSrc}
                                alt={orgName}
                                className="logo img-fluid"
                                style={{
                                    height: isOrgPortal ? "80px" : "112px",
                                    marginBottom: isOrgPortal ? "10px" : 0,
                                }}
                            />
                        </NavLink>}

                        {!isOrgPortal && (
                            <div
                                style={{
                                    color: colors.primary,
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: "24px",
                                    fontWeight: 800,
                                    letterSpacing: "0.1px",
                                    lineHeight: 1.1,
                                    marginTop: "6px",
                                }}
                            >
                                <BrandName
                                    size="inherit"
                                    sportsStyle={{marginLeft: 3}}
                                />
                            </div>
                        )}

                        {isOrgPortal && (
                            <h2
                                className="brand-text"
                                style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: colors.primary,
                                }}
                            >
                                <>
                                    {orgName}
                                    <span
                                        style={{
                                            display: "block",
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: colors.textOnSurface,
                                            marginTop: 2,
                                        }}
                                    >
                                        Powered by {" "}
                                        <BrandName size="inherit" sportsStyle={{marginLeft: 2}} />
                                    </span>
                                </>
                            </h2>
                        )}
                    </div>

                    <div>
                        <p
                            className="login-box-msg"
                            style={{
                                fontSize: "16px",
                                fontWeight: 500,
                                marginBottom: "20px",
                            }}
                        >
                            {t('auth.title')}
                        </p>

                        {/* Login Form */}
                        <Form
                            method="post"
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (loading) return;

                                userLogin({
                                    variables: { email, password },
                                })
                                    .then(({ data }) => {
                                        if (data && data.webLogin.success) {
                                            dispatch(userActions.login(data.webLogin));

                                            // TODO: uncomment this when campaign is ready
                                            // if (data.webLogin.user.sponsors?.length > 0) {
                                            //     return navigate('/advertise/sponsor');
                                            // }

                                            const from = location.state?.from;
                                            const destination = from
                                                ? `${from.pathname}${from.search || ""}${from.hash || ""}`
                                                : "/management/organizations";
                                            return navigate(destination, {replace: true});
                                        } else if (data && !data.webLogin.success) {
                                            toast.error(
                                                t('auth.toast.invalid'),
                                                {
                                                    autoClose: 5000,
                                                    toastId: "invalid_credentials",
                                                    position: "top-center",
                                                    closeOnClick: true,
                                                }
                                            );
                                        }
                                    })
                                    .catch((error) => {
                                        toast.error(t('signUp.toast.error'), {
                                            autoClose: 5000,
                                            toastId: "invalid_credentials",
                                            position: "top-center",
                                            closeOnClick: true,
                                        });
                                    });
                            }}
                        >
                            {/* Email */}
                            <div className="input-group mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t('auth.email')}
                                    name="email"
                                    value={email}
                                    disabled={loading}
                                    onChange={(event) =>
                                        setEmail(event.target.value)
                                    }
                                    required
                                    style={{
                                        backgroundColor: colors.surface,
                                        color: colors.textOnSurface,
                                        border: `1px solid ${
                                            colors.primary
                                        }`,
                                        borderRadius: "8px",
                                        padding: "12px",
                                        fontSize: "16px",
                                        outline: "none",
                                        width: "100%",
                                        transition: "all 0.3s ease",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor =
                                            colors.secondary;
                                        e.target.style.backgroundColor =
                                            colors.background;
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor =
                                            colors.secondary;
                                        e.target.style.backgroundColor =
                                            colors.background;
                                    }}
                                />
                            </div>

                            {/* Password */}
                            <div className="input-group mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder={t('auth.password')}
                                    name="password"
                                    value={password}
                                    disabled={loading}
                                    onChange={(event) =>
                                        setPassword(event.target.value)
                                    }
                                    required
                                    style={{
                                        backgroundColor: colors.surface,
                                        color: colors.textOnSurface,
                                        border: `1px solid ${
                                            colors.primary
                                        }`,
                                        borderRadius: "8px",
                                        padding: "12px",
                                        fontSize: "16px",
                                        outline: "none",
                                        width: "100%",
                                        transition: "all 0.3s ease",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor =
                                            colors.secondary;
                                        e.target.style.backgroundColor =
                                            colors.background;
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor =
                                            colors.secondary;
                                        e.target.style.backgroundColor =
                                            colors.background;
                                    }}
                                />
                            </div>

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-block"
                                style={{
                                    backgroundColor:
                                        colors.primary || "var(--kbs-primary, #00adff)",
                                    color:
                                        colors.textOnPrimary || "#232327",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    width: "100%",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    border: "none",
                                    opacity: loading ? 0.8 : 1,
                                }}
                                disabled={loading}
                                aria-busy={loading}
                                onMouseOver={(e) =>
                                    (e.target.style.backgroundColor =
                                        colors.secondary || "#c0e655")
                                }
                                onMouseOut={(e) =>
                                    (e.target.style.backgroundColor =
                                        colors.primary || "var(--kbs-primary, #00adff)")
                                }
                            >
                                {loading && (
                                    <span
                                        className="spinner-border spinner-border-sm mr-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}
                                {loading ? t("auth.signingIn") : t("auth.loginTitle")}
                            </button>
                        </Form>

                        {/* Forgot / Sign up / Resend activation */}
                        <div className="signup-container text-center mt-3">
                            <NavLink
                                to="/forgot-password"
                                className="text-center d-block"
                                style={{
                                    color: colors.primary || "var(--kbs-primary, #00adff)",
                                    fontWeight: "bold",
                                    textDecoration: "underline",
                                    fontSize: "14px",
                                    marginTop: "10px",
                                }}
                            >
                                {t("auth.forgotPassword")}
                            </NavLink>
                            <p
                                className="signup-text"
                                style={{
                                    marginTop: "10px",
                                    fontSize: "14px",
                                    color: colors.textOnSurface,
                                    fontWeight: "bold",
                                }}
                            >
                                {t("auth.new")}
                            </p>
                            <NavLink
                                to="/register"
                                className="signup-link"
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: colors.primary,
                                    textDecoration: "underline",
                                }}
                            >
                                {t("auth.signUpTitle")}
                            </NavLink>
                        </div>

                        <div
                            className="signup-container text-center mt-3"
                            style={{ fontSize: "14px", color: colors.primary, }}
                        >
                            {t("auth.didntReceived")}{" "}
                            <NavLink
                                to="/resend-activation"
                                className="text-center d-block"
                                style={{
                                    color: colors.primary,
                                    fontWeight: "bold",
                                    textDecoration: "underline",
                                    fontSize: "14px",
                                    margin: 12,
                                }}
                            >
                                {t("auth.resendActivation")}
                            </NavLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
